// @ts-strict-ignore
import { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import capitalize from 'lodash-es/capitalize'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { useFormFields } from 'hooks/forms/useFormFields'
import { usePostAcceptEstimateMutation } from 'hooks/mutations/usePostAcceptEstimate'
import { usePostEstimateMutation } from 'hooks/mutations/usePostEstimateMutation'
import { usePutEstimateMutation } from 'hooks/mutations/usePutEstimateMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useMinutesSecondsTimer } from 'hooks/useMinutesSecondsTimer'
import { contactAtom, contactInstrumentAtom } from 'pages/Contacts/atoms/contactAtoms'
import { getContactComplianceDetails } from 'pages/Contacts/utils/utils'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ErrorCodes } from 'types/errorCodes'
import { AcceptEstimatePayload, Estimate, EstimateResponse, PartyDetailsType } from 'types/payments'
import { ErrorAPI, ErrorResponseV2 } from 'types/types'
import { SendStep } from 'types/walletActions'
import { CryptoWithDestinationTag } from 'types/whitelisting'
import { generateReference } from 'utils/utils'
import { estimateIdAtom, paymentAtom, sendStepAtom, sendWalletAtom } from '../atoms/atoms'
import {
  SendDetailsFormData,
  SendDetailsFormKeys,
  sendDetailsSchema,
} from '../schemas/sendDetailsSchema'

export const useSendDetailsForm = () => {
  const { t } = useTranslation()
  const sendWallet = useAtomValue(sendWalletAtom)
  const contactInstrument = useAtomValue(contactInstrumentAtom)
  const contact = useAtomValue(contactAtom)
  const setSendStep = useSetAtom(sendStepAtom)
  const [estimateId, setEstimateId] = useAtom(estimateIdAtom)
  const [changedField, setChangedField] = useState('')
  const [source, setSource] = useState('user')
  const setPayment = useSetAtom(paymentAtom)
  const [estimate, setEstimate] = useState<EstimateResponse>(null)

  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const {
    mutate: postEstimate,
    isPending: isPostingEstimate,
    isError: isErrorPostingEstimate,
    error: ErrorPostingEstimate,
  } = usePostEstimateMutation()
  const {
    mutate: putEstimate,
    isPending: isUpdatingEstimate,
    isError: isErrorUpdatingEstimate,
    error: ErrorUpdatingEstimate,
  } = usePutEstimateMutation()
  const { mutate: postAcceptEstimate, isPending: isAcceptingEstimate } =
    usePostAcceptEstimateMutation()

  const formSchema = sendDetailsSchema(t)

  const form = useForm<SendDetailsFormData>({
    defaultValues: {
      sendAmount: '',
      receiveAmount: '',
      reference: generateReference(),
    },
    resolver: yupResolver(formSchema),
  })

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = form

  const formValues = useWatch({ control })

  useEffect(() => {
    if (ErrorUpdatingEstimate || ErrorPostingEstimate) {
      const err: BaseErrorResponse = ErrorUpdatingEstimate || ErrorPostingEstimate
      const axiosError = err as { data: ErrorAPI }
      const responseError = err as { data: ErrorResponseV2 }

      if (responseError?.data?.code === 'MER-PAY-2053') {
        setRequestError({
          handledMessage: t('wallets.send.somethingWentWrongMessage'),
          show: true,
        })
      }
      if (responseError?.data?.code === ErrorCodes.REFERENCE_SPECIAL_CHARACTERS) {
        setError('reference', { message: responseError?.data?.message })
      }
      if (axiosError?.data?.errorList && axiosError.data?.errorList?.length > 0) {
        const prefix = changedField === 'sendAmount' ? 'send' : 'receive'

        axiosError.data.errorList.forEach(axiosErr => {
          setError(`${prefix}${capitalize(axiosErr?.parameter)}` as keyof SendDetailsFormData, {
            message: axiosErr?.message,
          })
        })
      }
    }
  }, [ErrorUpdatingEstimate, ErrorPostingEstimate])

  const debouncedSendAmount = useDebounce(formValues?.sendAmount, 500)
  const debouncedReceiveAmount = useDebounce(formValues?.receiveAmount, 500)
  const debouncedReference = useDebounce(formValues?.reference, 500)

  const sameCurrency = contactInstrument?.currency === sendWallet?.currency?.code
  const shouldStart =
    sameCurrency || estimate?.pegged ? false : !!debouncedSendAmount && !!debouncedReceiveAmount
  const showTimer = sameCurrency
    ? !!debouncedSendAmount && !!debouncedReceiveAmount
    : !!debouncedSendAmount
  const isDisabledReviewPayment =
    !!Object.keys(errors)?.length ||
    isUpdatingEstimate ||
    isPostingEstimate ||
    isErrorPostingEstimate ||
    isErrorUpdatingEstimate
  const isLoadingReviewPayment =
    isAcceptingEstimate || isUpdatingEstimate || isPostingEstimate || isSubmitting
  const { timeLeft, resetTimer } = useMinutesSecondsTimer(0, 30, shouldStart)

  const { setInputField } = useFormFields<SendDetailsFormKeys>(formSchema, form, formValues)

  const formFields: { [key in SendDetailsFormKeys] } = {
    sendAmount: setInputField({
      key: 'sendAmount',
    }),
    receiveAmount: setInputField({
      key: 'receiveAmount',
    }),
    reference: setInputField({
      key: 'reference',
    }),
  }

  const handlePopulateFormValues = (estimate: EstimateResponse) => {
    setEstimate(estimate)

    const receiveAmount = estimate?.pegged
      ? estimate?.walletRequiredAmount
      : estimate?.paidRequiredAmount

    if (changedField === 'sendAmount') {
      setValue('receiveAmount', String(receiveAmount))
    }

    if (changedField === 'receiveAmount') {
      setValue('sendAmount', String(estimate?.walletRequiredAmount))
    }

    if (changedField === 'reference') {
      setValue('receiveAmount', String(receiveAmount))
      setValue('sendAmount', String(estimate?.walletRequiredAmount))
    }
    setSource('endpoint')
  }

  const handleEstimate = () => {
    if (isUpdatingEstimate || isPostingEstimate || isAcceptingEstimate) return
    if (!sameCurrency && !debouncedSendAmount && !debouncedReceiveAmount) return
    if (sameCurrency && !debouncedSendAmount) return
    resetRequestError()
    clearErrors()
    if (estimateId) {
      const data: Estimate = {
        ...(changedField === 'sendAmount' && {
          walletRequiredAmount: Number(formValues?.sendAmount),
        }),
        ...(changedField === 'receiveAmount' && {
          paidRequiredAmount: Number(formValues?.receiveAmount),
        }),
        ...(changedField === 'reference' && {
          walletRequiredAmount: Number(formValues?.sendAmount),
          paidRequiredAmount: Number(formValues?.receiveAmount),
        }),
        reference: formValues?.reference,
        complianceDetails: getContactComplianceDetails(
          contact,
          PartyDetailsType.BENEFICIARY,
          contactInstrument
        ),
      }
      putEstimate(
        { data, estimateId },
        {
          onSuccess: response => {
            handlePopulateFormValues(response)
            resetTimer()
          },
          onError: (error: BaseErrorResponse) => {
            const errorStatus = error?.status

            if (errorStatus !== 400) {
              const sentryEventId = logSentryErrorAndGetEventId(
                `Failed API request to /api/v1/pay/estimate with status ${error?.status} - putEstimate`,
                error,
                {
                  data: typeof data === 'string' ? { message: data } : data,
                }
              )

              setRequestError({
                handledMessage: error?.message,
                errorCode: error?.status,
                sentryTraceId: sentryEventId,
                show: true,
              })
            }
          },
        }
      )
    } else {
      const data: Estimate = {
        walletId: sendWallet?.lsid,
        ...(changedField === 'sendAmount' && {
          walletRequiredAmount: Number(formValues?.sendAmount),
        }),
        ...(changedField === 'receiveAmount' && {
          paidRequiredAmount: Number(formValues?.receiveAmount),
        }),
        walletCurrency: sendWallet?.currency?.code,
        paidCurrency: contactInstrument?.currency,
        reference: formValues?.reference,
        network: contactInstrument?.network,
        complianceDetails: getContactComplianceDetails(
          contact,
          PartyDetailsType.BENEFICIARY,
          contactInstrument
        ),
      }
      postEstimate(data, {
        onSuccess: response => {
          setEstimateId(response?.externalId)
          handlePopulateFormValues(response)
          resetTimer()
        },
        onError: (error: BaseErrorResponse) => {
          const errorStatus = error?.status

          if (errorStatus !== 400) {
            const sentryEventId = logSentryErrorAndGetEventId(
              `Failed API request to /api/v1/pay/estimate with status ${error?.status} - postEstimate`,
              error,
              {
                data: typeof data === 'string' ? { message: data } : data,
              }
            )
            setRequestError({
              handledMessage: error?.message,
              errorCode: error?.status,
              sentryTraceId: sentryEventId,
              show: true,
            })
          }
        },
      })
    }
  }

  useEffect(() => {
    if (source === 'endpoint') return

    handleEstimate()
  }, [debouncedReceiveAmount, debouncedSendAmount, debouncedReference])

  useEffect(() => {
    if (timeLeft) return

    handleEstimate()
  }, [timeLeft])

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    const mayRequireDestinationTag =
      contactInstrument?.currency === CryptoWithDestinationTag.XRP ||
      contactInstrument?.currency === CryptoWithDestinationTag.ALGO
    const data: AcceptEstimatePayload = {
      customerId: contact?.id,
      payOutDetails: {
        currency: contactInstrument?.currency,
        address: contactInstrument?.address,
        network: contactInstrument?.network,
        ...(mayRequireDestinationTag &&
          contactInstrument?.tag && {
            tag: contactInstrument?.tag,
          }),
      },
      complianceDetails: getContactComplianceDetails(
        contact,
        PartyDetailsType.BENEFICIARY,
        contactInstrument
      ),
    }

    postAcceptEstimate(
      { data, estimateId },
      {
        onSuccess: response => {
          setPayment(response)
          setSendStep(SendStep.REVIEW)
        },
        onError: (error: BaseErrorResponse) => {
          const errorStatus = error?.status
          let sentryEventId = null

          if (errorStatus !== 400) {
            sentryEventId = logSentryErrorAndGetEventId(
              `Failed API request to /api/v1/pay/estimate/{estimateId}/accept with status ${error?.status} - postAcceptEstimate`,
              error,
              {
                data: typeof data === 'string' ? { message: data } : data,
              }
            )
          }

          const errorMessage = error?.data?.message || error?.message
          const errorList = error?.data?.errorList

          if (errorList?.length) {
            setRequestError({
              handledMessage: errorList[0]?.message,
              sentryTraceId: sentryEventId,
              show: true,
            })
          } else {
            setRequestError({
              handledMessage: errorMessage,
              errorCode: error?.status,
              sentryTraceId: sentryEventId,
              show: true,
            })
          }
        },
      }
    )
  }

  return {
    formFields,
    estimate,
    errors,
    control,
    isSubmitting,
    isPostingEstimate,
    isUpdatingEstimate,
    isAcceptingEstimate,
    isDisabledReviewPayment,
    isLoadingReviewPayment,
    requestError,
    showTimer,
    timeLeft,
    sameCurrency,
    handleSubmit,
    onSubmit,
    setChangedField,
    setValue,
    setSource,
  }
}
